import { SaveDialogContent, SaveButton } from "@/components/SaveForm";
import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { ProtectedImage } from "@/components/ProtectedImage";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, Cog } from "lucide-react";

const inp = "bg-slate-950 border-slate-700 text-slate-100 mt-1";
const EMPTY = { nome: "", codigo: "", tipo: "", fabricante: "", modelo: "", numero_serie: "", ano: "", localizacao: "", status: "OPERANDO", foto: "" };
const STATUS = [
  { value: "OPERANDO", label: "Operando" },
  { value: "MANUTENCAO", label: "Manutenção" },
  { value: "PARADA", label: "Parada" },
];

function machineToForm(machine) {
  if (!machine) return EMPTY;
  return {
    nome: machine.nome || "",
    codigo: machine.codigo || "",
    tipo: machine.tipo || "",
    fabricante: machine.fabricante || "",
    modelo: machine.modelo || "",
    numero_serie: machine.numero_serie || "",
    ano: machine.ano || "",
    localizacao: machine.localizacao || "",
    status: machine.status || "OPERANDO",
    foto: machine.foto || "",
  };
}

export function MachineForm({ open, onOpenChange, machine, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadingRef = useRef(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setForm(machineToForm(machine));
  }, [open, machine]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploadingRef.current || savingRef.current) return;
    uploadingRef.current = true;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("foto", data.path);
      toast.success("Foto enviada.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      uploadingRef.current = false;
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (savingRef.current || uploadingRef.current) return;
    if (!form.nome.trim()) { toast.error("Nome é obrigatório."); return; }
    if (uploading) { toast.error("Aguarde o envio da foto terminar."); return; }
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        codigo: form.codigo || "",
        tipo: form.tipo || "",
        fabricante: form.fabricante || "",
        modelo: form.modelo || "",
        numero_serie: form.numero_serie || "",
        ano: form.ano ? Number(form.ano) : null,
        localizacao: form.localizacao || "",
        status: form.status || "OPERANDO",
        foto: form.foto || "",
      };
      if (machine) await api.put(`/machines/${machine.id}`, payload);
      else await api.post("/machines", payload);
      toast.success(machine ? "Máquina atualizada." : "Máquina cadastrada.");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <SaveDialogContent saveBlocked={saving || uploading}
        className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby={undefined}
      >
        <DialogHeader><DialogTitle className="font-display text-2xl">{machine ? "Editar máquina" : "Nova máquina"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-24 w-24 rounded-lg bg-slate-950 overflow-hidden border border-slate-700 shrink-0 flex items-center justify-center">
              {form.foto ? <ProtectedImage src={form.foto} alt="" className="h-full w-full object-cover" /> : <Cog className="h-8 w-8 text-slate-700" />}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm hover:bg-slate-800">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {form.foto ? "Substituir foto" : "Enviar foto"}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={upload} disabled={saving || uploading} data-testid="input-machine-photo" />
              </label>
              {form.foto && <Button type="button" size="sm" variant="ghost" onClick={() => set("foto", "")} className="text-rose-400 hover:text-rose-300 hover:bg-rose-950/40" data-testid="btn-remove-machine-photo"><Trash2 className="h-4 w-4 mr-1" /> Remover</Button>}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nome *</Label><Input data-testid="input-machine-nome" className={inp} value={form.nome} onChange={(e) => set("nome", e.target.value)} /></div>
            <div><Label>Código</Label><Input className={inp} value={form.codigo} onChange={(e) => set("codigo", e.target.value)} /></div>
            <div><Label>Tipo</Label><Input className={inp} value={form.tipo} onChange={(e) => set("tipo", e.target.value)} /></div>
            <div><Label>Fabricante</Label><Input className={inp} value={form.fabricante} onChange={(e) => set("fabricante", e.target.value)} /></div>
            <div><Label>Modelo</Label><Input className={inp} value={form.modelo} onChange={(e) => set("modelo", e.target.value)} /></div>
            <div><Label>Nº série</Label><Input className={inp} value={form.numero_serie} onChange={(e) => set("numero_serie", e.target.value)} /></div>
            <div><Label>Ano</Label><Input type="number" className={inp} value={form.ano} onChange={(e) => set("ano", e.target.value)} /></div>
            <div><Label>Localização</Label><Input className={inp} value={form.localizacao} onChange={(e) => set("localizacao", e.target.value)} /></div>
            <div><Label>Situação operacional</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className={inp} data-testid="select-machine-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <SaveButton data-testid="btn-save-machine" onClick={save} disabled={saving || uploading} className="bg-sky-700 hover:bg-sky-600">
            {saving ? "Salvando..." : uploading ? "Enviando foto..." : "Salvar"}
          </SaveButton>
        </DialogFooter>
      </SaveDialogContent>
    </Dialog>
  );
}
